import { useCallback, useEffect, useRef, useState } from "react";
import { convertPCM16ToBase64, floatTo16BitPCM } from "../utils/audio.js";
const TARGET_SAMPLE_RATE = 24000;
const SAMPLE_NOISE_FLOOR = 0.003;
function useRealTimeAudioRecorder(onAudioChunk, analytics) {
    const [isRecording, setIsRecording] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const mediaStreamRef = useRef(null);
    const audioTrackRef = useRef(null);
    const audioContextRef = useRef(null);
    const processorRef = useRef(null);
    const silentGainRef = useRef(null);
    const startRecording = useCallback(async ()=>{
        if (isRecording) return;
        if (audioContextRef.current) try {
            await audioContextRef.current.close();
            audioContextRef.current = null;
        } catch (err) {
            analytics.logError({
                loggedFrom: 'useRealTimeAudioRecorder.startRecording.cleanup',
                message: 'Error closing previous AudioContext',
                error: err instanceof Error ? err.message : String(err)
            });
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    noiseSuppression: true,
                    echoCancellation: true,
                    autoGainControl: true,
                    channelCount: 1
                }
            });
            mediaStreamRef.current = stream;
            const track = stream.getAudioTracks()[0];
            audioTrackRef.current = track;
            const audioContext = new AudioContext({
                sampleRate: TARGET_SAMPLE_RATE
            });
            const mediaStreamSource = audioContext.createMediaStreamSource(stream);
            const highPassFilter = audioContext.createBiquadFilter();
            highPassFilter.type = 'highpass';
            highPassFilter.frequency.value = 200;
            const lowPassFilter = audioContext.createBiquadFilter();
            lowPassFilter.type = 'lowpass';
            lowPassFilter.frequency.value = 4350;
            mediaStreamSource.connect(highPassFilter);
            highPassFilter.connect(lowPassFilter);
            const processorCode = `
        class NoiseGateProcessor extends AudioWorkletProcessor {
          constructor() {
            super();
            this.shouldProcess = true;
            this.sampleFloor = ${SAMPLE_NOISE_FLOOR};

            this.port.onmessage = (event) => {
              if (event.data?.type === 'terminate') {
                this.shouldProcess = false;
              }
            };
          }

          process(inputs) {
            if (!this.shouldProcess) {
              return false;
            }
            const input = inputs[0];
            if (input && input.length > 0) {
              const channelData = input[0];
              const cleaned = channelData.slice();
              for (let i = 0; i < cleaned.length; i++) {
                if (Math.abs(cleaned[i]) < this.sampleFloor) {
                  cleaned[i] = 0;
                }
              }
              this.port.postMessage(cleaned);
            }
            return true;
          }
        }
        registerProcessor('noise-gate-processor', NoiseGateProcessor);
      `;
            const blob = new Blob([
                processorCode
            ], {
                type: "application/javascript"
            });
            const url = URL.createObjectURL(blob);
            await audioContext.audioWorklet.addModule(url);
            URL.revokeObjectURL(url);
            const processorNode = new AudioWorkletNode(audioContext, 'noise-gate-processor');
            lowPassFilter.connect(processorNode);
            const silentGainNode = audioContext.createGain();
            silentGainNode.gain.value = 0;
            processorNode.connect(silentGainNode);
            silentGainNode.connect(audioContext.destination);
            silentGainRef.current = silentGainNode;
            processorNode.port.onmessage = (event)=>{
                if (!audioTrackRef.current?.enabled) return;
                const float32Array = event.data;
                const pcm16Array = floatTo16BitPCM(float32Array);
                const base64String = convertPCM16ToBase64(pcm16Array);
                onAudioChunk(base64String);
            };
            processorRef.current = processorNode;
            audioContextRef.current = audioContext;
            setIsRecording(true);
        } catch (error) {
            analytics.logError({
                loggedFrom: 'useRealTimeAudioRecorder.startRecording',
                message: 'Failed to start recording user audio',
                error: error instanceof Error ? error.message : String(error)
            });
            throw error;
        }
    }, [
        isRecording,
        onAudioChunk,
        analytics
    ]);
    const stopRecording = useCallback(()=>{
        setIsMuted(false);
        if (processorRef.current) {
            processorRef.current.port.postMessage({
                type: 'terminate'
            });
            processorRef.current.disconnect();
            processorRef.current = null;
        }
        if (silentGainRef.current) {
            silentGainRef.current.disconnect();
            silentGainRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close()?.catch(console.error);
            audioContextRef.current = null;
        }
        if (audioTrackRef.current) {
            audioTrackRef.current.stop();
            audioTrackRef.current = null;
        }
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((track)=>track.stop());
            mediaStreamRef.current = null;
        }
        setIsRecording(false);
    }, []);
    const toggleMute = useCallback(()=>{
        if (audioTrackRef.current) {
            const newMuteState = !isMuted;
            audioTrackRef.current.enabled = !newMuteState;
            setIsMuted(newMuteState);
        }
    }, [
        isMuted
    ]);
    useEffect(()=>()=>{
            if (isRecording) stopRecording();
        }, [
        isRecording,
        stopRecording
    ]);
    return {
        isRecording,
        isMuted,
        audioTrack: audioTrackRef.current,
        mediaStream: mediaStreamRef.current,
        audioContext: audioContextRef.current,
        startRecording,
        stopRecording,
        toggleMute
    };
}
export { useRealTimeAudioRecorder };
